import React from "react";
import ProductLinkValidator from "@/components/ProductLinkValidator";
import ProductLinkHealthCheck from "@/components/ProductLinkHealthCheck";
// import CartHealthCheck from "@/components/CartHealthCheck"; // DISABLED - Breaking /products page
import { Suspense, lazy } from "react";
// REMOVED shadcn Toaster - Using Sonner as OFFICIAL SINGLE SOURCE
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ThemeProvider } from 'next-themes';
import { AuthProvider } from "@/components/AuthContext";
import { PerformanceProvider } from "@/context/PerformanceContext";
import { SessionTimeoutWarning } from "@/components/SessionTimeoutWarning";
import GlobalErrorBoundary from "@/components/GlobalErrorBoundary";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { SecurityHeadersProvider } from "@/components/SecurityHeadersProvider";
import { ConversionTrackingProvider } from "@/components/analytics/ConversionTracking";
import AnalyticsConfig from "@/components/analytics/AnalyticsConfig";
import { analyticsConfig } from "@/utils/analytics-config";


import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollToTop from "./components/ScrollToTop";
import AccessibilityAnnouncer from "./components/AccessibilityAnnouncer";
import { SkipNavigationLink } from "./components/accessibility/SkipNavigationLink";

// Loading components
import { 
  LazyLoadingSpinner, 
  AdminLoadingSpinner
} from "@/components/LazyLoadingSpinner";

// Import new standardized loading system
import { PageLoading } from "@/components/LoadingSystem";

// Critical path imports (non-lazy)
import Index from "@/pages/Index";
import Auth from "@/pages/Auth";
import Admin from "@/pages/Admin";
import UnifiedProductsPage from "@/components/UnifiedProductsPage";
import Colors2025 from "@/pages/2025Colors";
import { QuickViewProvider } from "@/components/QuickViewProvider";
import { FloatingActions } from "@/components/FloatingActions";
import { CheckoutSuccessDetector } from "@/components/CheckoutSuccessDetector";


// Lazy imports
const ProductDetail = lazy(() => import("@/pages/ProductDetail"));
const ShopifyProductDetail = lazy(() => import("@/pages/ShopifyProductDetail"));
const Favorites = lazy(() => import("@/pages/Favorites"));
const Account = lazy(() => import("@/pages/Account"));
const Checkout = lazy(() => import("@/pages/Checkout"));
const OrderConfirmation = lazy(() => import("@/pages/OrderConfirmation"));
const Orders = lazy(() => import("@/pages/Orders"));
const Collections = lazy(() => import("@/pages/Collections"));
const GoddessCollection = lazy(() => import("@/pages/GoddessCollection"));
const ChromeHeartsCollection = lazy(() => import("@/pages/ChromeHeartsCollection"));
const SaleCollection = lazy(() => import("@/pages/SaleCollection"));
const HotChocolateDesignCollection = lazy(() => import("@/pages/HotChocolateDesignCollection"));
const Jewelry = lazy(() => import("@/pages/collections/Jewelry"));
const Necklaces = lazy(() => import("@/pages/collections/Necklaces"));
const Bracelets = lazy(() => import("@/pages/collections/Bracelets"));
const Rings = lazy(() => import("@/pages/collections/Rings"));
const Earrings = lazy(() => import("@/pages/collections/Earrings"));
const JewelrySets = lazy(() => import("@/pages/collections/JewelrySets"));
const Apparel = lazy(() => import("@/pages/collections/Apparel"));
const Tops = lazy(() => import("@/pages/collections/Tops"));
const Bottoms = lazy(() => import("@/pages/collections/Bottoms"));
const Dresses = lazy(() => import("@/pages/collections/Dresses"));
const OnePieces = lazy(() => import("@/pages/collections/OnePieces"));
const LoungeWear = lazy(() => import("@/pages/collections/LoungeWear"));
const Accessories = lazy(() => import("@/pages/collections/Accessories"));
const Bags = lazy(() => import("@/pages/collections/Bags"));
const Shoes = lazy(() => import("@/pages/collections/Shoes"));
const BathBody = lazy(() => import("@/pages/collections/BathBody"));
const Lifestyle = lazy(() => import("@/pages/collections/Lifestyle"));
const Furniture = lazy(() => import("@/pages/collections/Furniture"));
const Rugs = lazy(() => import("@/pages/collections/Rugs"));
const Textiles = lazy(() => import("@/pages/collections/Textiles"));
const Decor = lazy(() => import("@/pages/collections/Decor"));
const Outdoors = lazy(() => import("@/pages/collections/Outdoors"));

const About = lazy(() => import("@/pages/About"));
const Contact = lazy(() => import("@/pages/Contact"));
const WarmTheme = lazy(() => import("@/pages/WarmTheme"));
const MutedTheme = lazy(() => import("@/pages/MutedTheme"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const SlowLiving = lazy(() => import("@/pages/SlowLiving"));
const SizeGuide = lazy(() => import("@/pages/SizeGuide"));
const ShippingInfo = lazy(() => import("@/pages/ShippingInfo"));
const ReturnsFAQ = lazy(() => import("@/pages/ReturnsFAQ"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const Blog = lazy(() => import("@/pages/Blog"));

const JewelrySize = lazy(() => import("@/pages/size-guide/Jewelry"));
const AccessoriesSize = lazy(() => import("@/pages/size-guide/Accessories"));
const TrackOrder = lazy(() => import("@/pages/TrackOrder"));
const Sitemap = lazy(() => import("@/pages/Sitemap"));
const LogoDisplay = lazy(() => import("@/pages/LogoDisplay"));
const VoiceMessages = lazy(() => import("@/pages/VoiceMessages"));
const PixelDebugger = lazy(() => import("@/pages/admin/PixelDebugger"));
const ShopByColor = lazy(() => import("@/pages/ShopByColor"));
const ShopifyInventoryAnalyzer = lazy(() => import("@/pages/admin/ShopifyInventoryAnalyzer"));

// Admin page imports
const AdminPages = {
  AdminConfig: lazy(() => import("@/pages/admin/AdminConfig")),
  AdminPortfolio: lazy(() => import("@/pages/admin/AdminPortfolio")),
  AdminSuppliers: lazy(() => import("@/pages/admin/AdminSuppliers")),
  TechnicalInventory: lazy(() => import("@/pages/admin/TechnicalInventory")),
  TagFirstAssignment: lazy(() => import("@/pages/admin/TagFirstAssignment")),
  AdminOrders: lazy(() => import("@/pages/admin/AdminOrders")),
  AdminNewsletter: lazy(() => import("@/pages/admin/AdminNewsletter")),
  AdminUsers: lazy(() => import("@/pages/admin/AdminUsers")),
  AdminSecurity: lazy(() => import("@/pages/admin/AdminSecurity")),
  AdminBadges: lazy(() => import("@/pages/admin/AdminBadges")),
  RestoreGuide: lazy(() => import("@/pages/admin/RestoreGuide")),
  AdminKnowledgeBase: lazy(() => import("@/pages/admin/AdminKnowledgeBase")),
  MarkdownEditor: lazy(() => import("@/pages/admin/MarkdownEditor")),
  AdminBackup: lazy(() => import("@/pages/admin/AdminBackup")),
  AdminBackupConfig: lazy(() => import("@/pages/admin/AdminBackupConfig")),
  AdminBackupManual: lazy(() => import("@/pages/admin/AdminBackupManual")),
  AdminBackupSchedule: lazy(() => import("@/pages/admin/AdminBackupSchedule")),
  AdminPerformance: lazy(() => import("@/pages/admin/AdminPerformance")),
  AdminBackupStatus: lazy(() => import("@/pages/admin/AdminBackupStatus")),
  AdminRateLimits: lazy(() => import("@/pages/admin/AdminRateLimits")),
  UXTesting: lazy(() => import("@/pages/admin/UXTesting")),
  AdminAnalytics: lazy(() => import("@/pages/admin/AdminAnalytics")),
  AdminHomepageImages: lazy(() => import("@/pages/admin/AdminHomepageImages")),
  FacebookCover: lazy(() => import("@/pages/admin/FacebookCover")),
  ShopifyEnhancements: lazy(() => import("@/pages/admin/ShopifyEnhancements"))
};

const BlogManagement = lazy(() => import("@/pages/admin/BlogManagement"));
const BlogEditor = lazy(() => import("@/pages/admin/BlogEditor"));



// Dynamic blog system
const DynamicBlogPost = lazy(() => import("@/pages/blog/DynamicBlogPost"));


// Import ShopifyCartProvider directly for reliability
import { ShopifyCartProvider } from "@/hooks/useShopifyCart";
import { FavoritesProvider } from "@/contexts/FavoritesContext";
import { FloatingActionsProvider } from "@/contexts/FloatingActionsContext";
// REMOVED UnifiedCartProvider - Shopify-only cart system now

// Lazy load Admin Image Optimizer
const AdminImageOptimizer = lazy(() => import("@/pages/admin/ImageOptimizer"));

const BlogPostsList = lazy(() => import("@/pages/admin/BlogPostsList"));
const TestRompers = lazy(() => import("@/pages/admin/TestRompers"));

// 2025 Design Demo Page
const Home2025 = lazy(() => import("@/pages/Home2025"));

// Dynamic blog system

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (was cacheTime)
      retry: 1, // Reduce retries for faster failures
    },
  },
});

const LegacyProductRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/products/${slug}`} replace />;
};

const App = () => {
  // Remove session initialization completely from the render path - 
  // Let components initialize sessions as needed
  
  return (
  <GlobalErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
          <PerformanceProvider enableAutoMonitoring={true}>
            <SecurityHeadersProvider
            enableGeoBlocking={false}
            allowedCountries={['US', 'CA', 'GB', 'AU', 'DE', 'FR', 'JP']}
            enableRateMonitoring={true}
          >
            <AuthProvider>
              <FavoritesProvider>
                <FloatingActionsProvider>
                  <ConversionTrackingProvider>
                  <ShopifyCartProvider>
                  <QuickViewProvider>
                    <TooltipProvider>
                      {/* <CartHealthCheck /> */} {/* DISABLED - Causing runtime error */}
                  <AnalyticsConfig 
                    googleAnalyticsId={analyticsConfig.googleAnalytics.measurementId}
                    facebookPixelIds={analyticsConfig.facebook.pixelIds}
                  />
                  <Sonner />
                  <BrowserRouter>
                    <SkipNavigationLink />
                    <CheckoutSuccessDetector />
                    <ProductLinkValidator />
                    <ProductLinkHealthCheck />
                     <AccessibilityAnnouncer />
                     <ScrollToTop />
                   <Routes>
                  {/* Critical path routes (non-lazy) */}
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                   <Route path="/products" element={<UnifiedProductsPage />} />
                    <Route path="/collections" element={
                       <Suspense fallback={<PageLoading variant="minimal" title="Loading Collections..." />}>
                        <Collections />
                      </Suspense>
                    } />
                    <Route path="/shop-by-color" element={
                      <Suspense fallback={<PageLoading variant="minimal" title="Loading Color Collections..." />}>
                        <ShopByColor />
                      </Suspense>
                    } />
                    <Route path="/collections/goddess" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <GoddessCollection />
                      </Suspense>
                    } />
                    <Route path="/collections/chrome-hearts" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <ChromeHeartsCollection />
                      </Suspense>
                    } />
                    <Route path="/collections/sale" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <SaleCollection />
                      </Suspense>
                    } />
                    <Route path="/collections/hot-chocolate-designs" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <HotChocolateDesignCollection />
                      </Suspense>
                    } />
                    <Route path="/collections/jewelry" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Jewelry />
                      </Suspense>
                    } />
                    <Route path="/collections/necklaces" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Necklaces />
                      </Suspense>
                    } />
                    <Route path="/collections/bracelets" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Bracelets />
                      </Suspense>
                    } />
                    <Route path="/collections/rings" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Rings />
                      </Suspense>
                    } />
                    <Route path="/collections/earrings" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Earrings />
                      </Suspense>
                    } />
                    <Route path="/collections/jewelry-sets" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <JewelrySets />
                      </Suspense>
                    } />
                    <Route path="/collections/apparel" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Apparel />
                      </Suspense>
                    } />
                    <Route path="/collections/tops" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Tops />
                      </Suspense>
                    } />
                    <Route path="/collections/bottoms" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Bottoms />
                      </Suspense>
                    } />
                    <Route path="/collections/dresses" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Dresses />
                      </Suspense>
                    } />
                    <Route path="/collections/one-pieces" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <OnePieces />
                      </Suspense>
                    } />
                    <Route path="/collections/lounge-wear" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <LoungeWear />
                      </Suspense>
                    } />
                    <Route path="/collections/accessories" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Accessories />
                      </Suspense>
                    } />
                    <Route path="/collections/bags" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Bags />
                      </Suspense>
                    } />
                    <Route path="/collections/shoes" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Shoes />
                      </Suspense>
                    } />
                    <Route path="/collections/bath-body" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <BathBody />
                      </Suspense>
                    } />
                    <Route path="/collections/lifestyle" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Lifestyle />
                      </Suspense>
                    } />
                    <Route path="/collections/furniture" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Furniture />
                      </Suspense>
                    } />
                    <Route path="/collections/rugs" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Rugs />
                      </Suspense>
                    } />
                    <Route path="/collections/textiles" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Textiles />
                      </Suspense>
                    } />
                    <Route path="/collections/decor" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Decor />
                      </Suspense>
                    } />
                    <Route path="/collections/outdoors" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Outdoors />
                      </Suspense>
                    } />
                  
                    {/* Lazy-loaded public routes */}
                    <Route path="/about" element={
                     <Suspense fallback={<LazyLoadingSpinner />}>
                       <About />
                     </Suspense>
                   } />
                     <Route path="/contact" element={
                       <Suspense fallback={<LazyLoadingSpinner />}>
                         <Contact />
                       </Suspense>
                     } />
                      <Route path="/2025-colors" element={
                        <Colors2025 />
                      } />
                    <Route path="/warm-theme" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <WarmTheme />
                      </Suspense>
                    } />
                    <Route path="/muted-theme" element={
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <MutedTheme />
                      </Suspense>
                    } />
                  <Route path="/products/:slug" element={
                    <Suspense fallback={<LazyLoadingSpinner />}>
                      <ProductDetail />
                    </Suspense>
                  } />
                  <Route path="/product/:slug" element={<LegacyProductRedirect />} />
                  <Route path="/shopify-product/:handle" element={
                    <Suspense fallback={<LazyLoadingSpinner />}>
                      <ShopifyProductDetail />
                    </Suspense>
                  } />
                  
                  {/* Protected user routes */}
                  <Route path="/favorites" element={
                    <ProtectedRoute>
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Favorites />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/account" element={
                    <ProtectedRoute>
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Account />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/checkout" element={
                    <ProtectedRoute>
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Checkout />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/orders" element={
                    <ProtectedRoute>
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <Orders />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/order-confirmation/:orderId" element={
                    <ProtectedRoute>
                      <Suspense fallback={<LazyLoadingSpinner />}>
                        <OrderConfirmation />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  
                  {/* Admin Routes - Grouped with admin-specific loading */}
                  <Route path="/admin" element={
                    <ProtectedRoute requireAdmin>
                       <Suspense fallback={<PageLoading variant="admin" title="Loading Admin Dashboard..." />}>
                        <Admin />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/config" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminConfig />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/portfolio" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminPortfolio />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/suppliers" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminSuppliers />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/tech-inventory" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.TechnicalInventory />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/tag-first-assignment" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.TagFirstAssignment />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/orders" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminOrders />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/newsletter" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminNewsletter />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/users" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminUsers />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/security" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminSecurity />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/badges" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminBadges />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/restore-guide" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.RestoreGuide />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/knowledge-base" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminKnowledgeBase />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/markdown-editor" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.MarkdownEditor />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/backup" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminBackup />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/backup/config" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminBackupConfig />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/backup/manual" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminBackupManual />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/backup/schedule" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminBackupSchedule />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/image-optimizer" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminImageOptimizer />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/performance" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminPerformance />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/backup/status" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminBackupStatus />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/rate-limits" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminRateLimits />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/ux-testing" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.UXTesting />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/analytics" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminAnalytics />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/homepage-images" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.AdminHomepageImages />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/facebook-cover" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.FacebookCover />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/blog-posts" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <BlogPostsList />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/blog/new" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <BlogEditor />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/blog/:id/edit" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <BlogEditor />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/voice-messages" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <VoiceMessages />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/pixel-debugger" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <PixelDebugger />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/test-rompers" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <TestRompers />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/shopify-enhancements" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <AdminPages.ShopifyEnhancements />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                  <Route path="/admin/inventory-analyzer" element={
                    <ProtectedRoute requireAdmin>
                      <Suspense fallback={<AdminLoadingSpinner />}>
                        <ShopifyInventoryAnalyzer />
                      </Suspense>
                    </ProtectedRoute>
                  } />
                   
                   {/* 2025 Design Demo */}
                   <Route path="/home-2025" element={
                     <Suspense fallback={<LazyLoadingSpinner />}>
                       <Home2025 />
                     </Suspense>
                   } />
                   <Route path="/slow-living" element={
                     <Suspense fallback={<LazyLoadingSpinner />}>
                       <SlowLiving />
                     </Suspense>
                   } />
                   <Route path="/size-guide" element={
                    <Suspense fallback={<LazyLoadingSpinner />}>
                      <SizeGuide />
                    </Suspense>
                  } />
                  {/* Redirect old clothing size guide URL to main size guide */}
                  <Route path="/size-guide/clothing" element={<Navigate to="/size-guide" replace />} />
                  <Route path="/shipping-info" element={
                    <Suspense fallback={<LazyLoadingSpinner />}>
                      <ShippingInfo />
                    </Suspense>
                  } />
                   <Route path="/returns" element={
                     <Suspense fallback={<LazyLoadingSpinner />}>
                       <ReturnsFAQ />
                     </Suspense>
                    } />
                    <Route path="/faq" element={
                     <Suspense fallback={<LazyLoadingSpinner />}>
                       <FAQ />
                     </Suspense>
                    } />
                     <Route path="/blog" element={
                     <Suspense fallback={<LazyLoadingSpinner />}>
                       <Blog />
                     </Suspense>
                     } />
                     {/* Dynamic Blog Route - handles all blog posts from database */}
                     <Route path="/blog/:slug" element={<Suspense fallback={<LazyLoadingSpinner />}><DynamicBlogPost /></Suspense>} />
                       
                    <Route path="/size-guide/jewelry" element={
                     <Suspense fallback={<LazyLoadingSpinner />}>
                       <JewelrySize />
                     </Suspense>
                    } />
                    <Route path="/size-guide/accessories" element={
                     <Suspense fallback={<LazyLoadingSpinner />}>
                       <AccessoriesSize />
                     </Suspense>
                    } />
                    <Route path="/returns-faq" element={<Navigate to="/returns" replace />} />
                   <Route path="/track-order" element={
                    <Suspense fallback={<LazyLoadingSpinner />}>
                      <TrackOrder />
                    </Suspense>
                  } />
                  <Route path="/sitemap" element={
                    <Suspense fallback={<LazyLoadingSpinner />}>
                      <Sitemap />
                    </Suspense>
                  } />
                  <Route path="/backup" element={<Navigate to="/admin/backup" replace />} />
                  
                  
                  {/* Catch-all route */}
                  <Route path="*" element={
                    <Suspense fallback={<LazyLoadingSpinner />}>
                      <NotFound />
                    </Suspense>
                  } />
                </Routes>
                <FloatingActions />
              </BrowserRouter>
                  <SessionTimeoutWarning />
                  <PWAInstallPrompt />
                     </TooltipProvider>
                  </QuickViewProvider>
                  </ShopifyCartProvider>
                  </ConversionTrackingProvider>
                  </FloatingActionsProvider>
                </FavoritesProvider>
              </AuthProvider>
            </SecurityHeadersProvider>
          </PerformanceProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </GlobalErrorBoundary>
  );
};

export default App;